<template>
  <div class="video-viewer_container">
    <div v-if="videoUrl">
      <video
        ref="videoPlayer"
        :src="videoUrl"
        controls
        preload="auto"
        autoplay
        muted     
      ></video>
    </div>
    <p v-else>No video loaded...</p>
  </div>
</template>

<script setup>
import { ref, watchEffect, nextTick } from "vue";
import { useStore } from "vuex";

const props = defineProps({
  documentName: String,
});

const store = useStore();
const videoUrl = ref(null);
const videoPlayer = ref(null);

// Convert file bytes into a usable Blob
const getVideoBlob = (fileBytes, fileName) => {
  if (!fileBytes) return null;

  const ext = fileName?.split(".").pop()?.toLowerCase();

  const mimeType =
    {
      mp4: "video/mp4",
      webm: "video/webm",
      ogg: "video/ogg",
      ogv: "video/ogg",
      m4v: "video/mp4",
      m4u: "video/mp4",     // fallback for your request
    }[ext] || "video/mp4";   // default

  if (fileBytes instanceof Blob) return fileBytes;
  if (fileBytes instanceof ArrayBuffer) return new Blob([fileBytes], { type: mimeType });
  if (fileBytes instanceof Uint8Array) return new Blob([fileBytes], { type: mimeType });

  // Base64 fallback
  if (typeof fileBytes === "string") {
    const byteCharacters = atob(fileBytes);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }

  return null;
};

// Watch for video source changes
watchEffect(async () => {
  const doc = store.getters.getDocumentById(props.documentName);
  if (!doc) {
    videoUrl.value = null;
    return;
  }

  const blob = getVideoBlob(doc.FileBytes, doc.FileName);
  if (!blob) {
    videoUrl.value = null;
    return;
  }

  // Clean up old URL
  if (videoUrl.value) URL.revokeObjectURL(videoUrl.value);

  videoUrl.value = URL.createObjectURL(blob);

  // Autoplay logic after DOM updates
  await nextTick();

  if (videoPlayer.value) {
    videoPlayer.value.play().catch((err) => {
      console.warn("Autoplay was blocked:", err);
    });
  }
});
</script>

<style scoped>
.video-viewer_container {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  padding: 10px;
  border-radius: 4px;
}

video {
  width: 100%;
  max-width: 800px;
  background: #000;
  border-radius: 4px;
}
</style>
